import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useSupportConversations } from '@/features/support/api';
import type { SupportListFilter } from '@/features/support/api';
import type { SupportConversationStatus } from '@honeycomb/shared';

export const Route = createFileRoute('/_authed/support/')({
  component: SupportInboxPage,
});

// ── Filter chip definitions ────────────────────────────────────────────────────

type FilterDef = {
  value: SupportListFilter;
  label: string;
  testId: string;
};

const FILTERS: FilterDef[] = [
  { value: 'all',                  label: 'All',                testId: 'support-filter-all' },
  { value: 'needs_reply',          label: 'Needs reply',        testId: 'support-filter-needs-reply' },
  { value: 'waiting_on_customer',  label: 'Waiting on customer', testId: 'support-filter-waiting-on-customer' },
  { value: 'spam',                 label: 'Spam',               testId: 'support-filter-spam' },
  { value: 'closed',               label: 'Closed',             testId: 'support-filter-closed' },
];

// ── Status chip styling ───────────────────────────────────────────────────────

function statusChipStyle(
  status: SupportConversationStatus,
  isSpam: boolean,
): React.CSSProperties {
  if (isSpam) {
    return { backgroundColor: '#000', color: '#fff' };
  }
  switch (status) {
    case 'needs_reply':
      return { backgroundColor: '#FF746C', color: '#000' };
    case 'waiting_on_customer':
      return { backgroundColor: '#B3EBF2', color: '#000' };
    case 'closed':
      return { backgroundColor: '#80EF80', color: '#000' };
    default:
      return { backgroundColor: '#FF746C', color: '#000' };
  }
}

function statusLabel(status: SupportConversationStatus, isSpam: boolean): string {
  if (isSpam) return 'spam';
  return status.replace(/_/g, ' ');
}

// ── Timestamp formatting ──────────────────────────────────────────────────────

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays < 7) {
    return d.toLocaleDateString([], { weekday: 'short' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

// ── Page component ────────────────────────────────────────────────────────────

function SupportInboxPage() {
  const [filter, setFilter] = useState<SupportListFilter>('needs_reply');
  const { data, isLoading } = useSupportConversations({ filter });
  const conversations = data?.conversations ?? [];

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <header className="mb-6">
        <h1 className="font-serif text-3xl leading-tight">Support</h1>
        {!isLoading && (
          <p className="text-ink-soft text-sm mt-1">
            {conversations.length} conversation{conversations.length === 1 ? '' : 's'}
          </p>
        )}
      </header>

      {/* Filter chip bar */}
      <div
        data-testid="support-filter-bar"
        className="flex flex-wrap gap-2 mb-4"
      >
        {FILTERS.map((f) => (
          <button
            key={f.value}
            data-testid={f.testId}
            data-active={filter === f.value ? 'true' : 'false'}
            onClick={() => setFilter(f.value)}
            className={[
              'text-sm rounded-full px-3 py-1 border transition-colors',
              filter === f.value
                ? 'bg-honey text-ink border-honey font-medium'
                : 'bg-paper text-ink-soft border-hairline hover:bg-honey-tint',
            ].join(' ')}
            style={
              filter === f.value
                ? { borderColor: 'var(--color-honey)' }
                : { borderColor: 'var(--color-hairline)' }
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      <div
        data-testid="support-inbox"
        className="flex flex-col gap-2 md:max-w-2xl"
      >
        {isLoading ? (
          <div className="text-ink-soft py-6">Loading…</div>
        ) : conversations.length === 0 ? (
          <div className="text-ink-soft py-6">No conversations yet.</div>
        ) : (
          <>
          {conversations.map((conv) => (
            <Link
              key={conv.id}
              to="/support/$id"
              params={{ id: conv.id }}
              data-testid={`support-row-${conv.id}`}
              className="block rounded-xl border bg-paper px-4 py-3 hover:bg-honey-tint transition-colors"
              style={{ borderColor: 'var(--color-hairline)' }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  {/* Sender + unread indicator */}
                  <div className="flex items-center gap-2">
                    {conv.unread && (
                      <span
                        data-testid={`support-row-unread-${conv.id}`}
                        className="inline-block w-2 h-2 rounded-full bg-honey flex-none"
                        aria-label="Unread"
                      />
                    )}
                    <span
                      data-testid={`support-row-sender-${conv.id}`}
                      className="font-medium text-sm truncate"
                    >
                      {conv.senderName ?? conv.senderEmail}
                    </span>
                  </div>

                  {/* Subject */}
                  <div
                    data-testid={`support-row-subject-${conv.id}`}
                    className="text-sm text-ink mt-0.5 truncate"
                  >
                    {conv.subject}
                  </div>

                  {/* Snippet */}
                  <div
                    data-testid={`support-row-snippet-${conv.id}`}
                    className="text-xs text-ink-soft mt-1 line-clamp-1"
                  >
                    {conv.snippet}
                  </div>
                </div>

                {/* Right column: timestamp + status pill + receivedTo username */}
                <div className="flex flex-col items-end gap-1 flex-none">
                  <span
                    data-testid={`support-row-timestamp-${conv.id}`}
                    className="text-xs text-ink-faint tabular-nums"
                  >
                    {formatTimestamp(conv.lastMessageAt)}
                  </span>
                  <span
                    data-testid={`support-row-status-${conv.id}`}
                    className="text-xs rounded-full px-2 py-0.5"
                    style={statusChipStyle(conv.status, conv.isSpam)}
                  >
                    {statusLabel(conv.status, conv.isSpam)}
                  </span>
                  {conv.receivedToAddress != null && (
                    <span
                      data-testid={`support-row-to-username-${conv.id}`}
                      className="text-xs text-ink-faint"
                    >
                      {conv.receivedToAddress.split('@')[0]}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
          </>
        )}
      </div>
    </div>
  );
}
